import { forwardRef, FC, ForwardedRef } from 'react';
import styled from 'styled-components';

import { HtmlEditor } from 'modules/graphql/types';
import { body2, heading4, spacing, colors, shadows } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { HelpTooltip } from 'ui/tooltip';
import noop from 'utils/noop';

const OptionWrapper = styled.div`
  display: inline-flex;
  padding: ${spacing.plusHalf};
  flex-grow: 1;
  flex-direction: column;
  justify-content: center;

  align-content: space-around;
  border-radius: ${spacing.half};
  background: ${colors.grey1};
  border: ${pxToRem(3)} solid ${colors.white};

  :focus {
    outline: none;
    ${({ isSelected }: { isSelected: boolean }) =>
      !isSelected &&
      `
      border: ${pxToRem(1.5)} solid ${colors.grey50};
      margin: ${pxToRem(1.5)};
  `}
  }

  ${({ isSelected }: { isSelected: boolean }) =>
    isSelected &&
    `
    border: ${pxToRem(3)} solid ${colors.purple50};
  `}
`;

const OptionText = styled.span`
  ${heading4}
  margin-right: ${spacing.half};
`;

const BlockWrapper = styled.div`
  align-items: center;
  display: inline-flex;
  justify-content: center;
`;

const IllustrationWrapper = styled(BlockWrapper)`
  svg {
    filter: ${shadows.medium.map((shadow) => `drop-shadow(${shadow})`).join(' ')};
  }
`;

const LabelWrapper = styled(BlockWrapper)`
  margin-top: ${spacing.plusHalf};
`;

const LevelWrapper = styled(BlockWrapper)`
  margin-top: ${spacing.plusHalf};
  background: ${colors.white};
  border-radius: ${pxToRem(6)};
  padding: ${spacing.threeQuarter};
`;

const LevelText = styled.span`
  ${body2}
  margin-left: ${spacing.half};
`;

export type ContentEditorOptionProps = {
  content: {
    Image: FC<React.PropsWithChildren<unknown>>;
    Level: FC<React.PropsWithChildren<unknown>>;
    levelText: string;
    title: string;
    tooltip: string;
  };
  contentEditor: HtmlEditor;
  selectedEditor: HtmlEditor;
  setSelectedEditor: (htmlEditor: HtmlEditor) => void;
  autoFocus?: boolean;
};

export const ContentEditorOption = forwardRef(
  (
    {
      setSelectedEditor = noop,
      selectedEditor,
      content,
      contentEditor,
      autoFocus = false,
      ...props
    }: ContentEditorOptionProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => (
    <OptionWrapper
      tabIndex={0}
      onClick={() => setSelectedEditor(contentEditor)}
      isSelected={contentEditor === selectedEditor}
      ref={ref}
      autoFocus={autoFocus}
      {...props}
    >
      <IllustrationWrapper>
        <content.Image />
      </IllustrationWrapper>
      <LabelWrapper>
        <OptionText>{content.title}</OptionText>
        <HelpTooltip tooltipContent={content.tooltip} />
      </LabelWrapper>
      <LevelWrapper>
        <content.Level />
        <LevelText>{content.levelText}</LevelText>
      </LevelWrapper>
    </OptionWrapper>
  ),
);
